import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Dropdown, Icon, Table } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";

import util from "utils/utils";

import { SelectIdeaChip } from "components/lib/Cards/IdeaCard/ListView/styles";
import { FakeLink } from "components/lib/UI";

import type { LocalAssignments } from "./AssignmentDashboard";

type AssessmentIdeaRowProps = {
  idea: OpenAPI.Schemas["Idea"];
  localUserAssignments: LocalAssignments;
  localEmailAssignments: LocalAssignments;
  onInspect: (user: OpenAPI.Schemas["Idea"]) => void;
  onPreview: () => void;
  isSelected: boolean;
  onSelect: () => void;
  emailInvites?: any;
};

export const AssessmentIdeaRow = ({
  idea,
  localUserAssignments,
  localEmailAssignments,
  onInspect,
  onPreview,
  isSelected,
  onSelect,
  emailInvites,
}: AssessmentIdeaRowProps) => {
  const { t } = useTranslation();
  const [assignmentUserCount, assignmentEmailCount, assignmentCount] =
    useMemo(() => {
      const users = new Set<string>();
      const emails = new Set<string>();
      let count = 0;
      for (const assignment of idea.assessments || []) {
        users.add(assignment.user);
        count += 1;
      }
      for (const emailInvite of emailInvites || []) {
        for (const ideaId of emailInvite.forIdeas) {
          if (ideaId === idea._id) {
            count += 1;
            emails.add(emailInvite.email);
          }
        }
      }
      return [users.size, emails.size, count];
    }, [idea.assessments, emailInvites, idea._id]);

  const [localUserCount, localCount] = useMemo(() => {
    let userCount = 0;
    let count = 0;
    for (const assignment of Object.values(localUserAssignments || {})) {
      for (const [ideaId, assign] of Object.entries(assignment.ideas || {})) {
        if (ideaId === idea._id) {
          userCount += 1;
          count += assign.assessmentRoundIds.length;
        }
      }
    }
    for (const assignment of Object.values(localEmailAssignments || {})) {
      for (const [ideaId, assign] of Object.entries(assignment.ideas || {})) {
        if (ideaId === idea._id) {
          userCount += 1;
          count += assign.assessmentRoundIds.length;
        }
      }
    }
    return [userCount, count];
  }, [idea._id, localUserAssignments, localEmailAssignments]);
  return (
    <Table.Row key={idea._id}>
      <Table.Cell style={{ width: 30 }}>
        <SelectIdeaChip
          $compact
          $isSelected={isSelected}
          onClick={() => {
            onSelect();
          }}
        >
          <Icon name="check" />
        </SelectIdeaChip>
      </Table.Cell>
      <Table.Cell>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            gap: 6,
            paddingRight: 12,
          }}
        >
          <ImageWithFallback
            avatar
            style={{
              minWidth: 35,
              width: 35,
              height: 25,
              borderRadius: 4,
              objectFit: "cover",
            }}
            src={util.ideaCoverImage(idea, "200x200")}
            fallbackSrc={util.ideaCoverImage()}
          />
          <div>
            <div
              style={{
                fontSize: "1em",
                lineHeight: "16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {idea.name}
            </div>
            {(
              idea.assessments?.length > 0 ||
              localCount > 0 ||
              assignmentCount > 0
            ) ?
              <FakeLink
                style={{ fontSize: 12, lineHeight: "14px" }}
                onClick={() => {
                  onInspect({
                    ...idea,
                    emailInvites: emailInvites?.filter((invite: any) =>
                      invite.forIdeas.includes(idea._id),
                    ),
                  });
                }}
              >
                {util.pluralise(assignmentCount, "assessment", "assessments")}
                {assignmentCount > 0 ?
                  ` (${util.pluralise(assignmentUserCount, "user", "users")}` +
                  (assignmentEmailCount ?
                    `, ${util.pluralise(assignmentEmailCount, "email invite", "email invites")}`
                  : "") +
                  `)`
                : ""}
                {localCount > 0 ?
                  `, ${util.pluralise(localCount, "draft assignment", "draft assignments")}`
                : ""}
                {localCount > 0 ?
                  ` (${util.pluralise(localUserCount, "user", "users")})`
                : ""}
              </FakeLink>
            : null}
          </div>
        </div>
      </Table.Cell>
      <Table.Cell style={{ width: 40, overflow: "visible" }} collapsing>
        <Dropdown
          icon={null}
          trigger={<Icon name="chevron down" />}
          direction="left"
        >
          <Dropdown.Menu>
            <Dropdown.Item
              key="inspect"
              onClick={() => {
                onPreview();
              }}
            >
              <Icon name="eye" /> View {t("generic.idea")}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </Table.Cell>
    </Table.Row>
  );
};
